'use client';

import { Share2, MoreVertical, Globe, Lock, Trash2, Database, Link, Check } from 'lucide-react';
import { Button } from '@/_components/UIComponents/Button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/_components/UIComponents/DropdownMenu';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/_components/UIComponents/AlertDialog';
import DeleteConversationDialog from '@/_components/DeleteConversationDialog/DeleteConversationDialog';
import { useConversationNavbar } from './useConversationNavbar.client';

const XIcon = ({ className }: { className?: string }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='currentColor'
	>
		<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
	</svg>
);

const TelegramIcon = ({ className }: { className?: string }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='currentColor'
	>
		<path d='M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z' />
	</svg>
);

const RedditIcon = ({ className }: { className?: string }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='currentColor'
	>
		<path d='M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z' />
	</svg>
);

function ShareMenuItems({
	isPublic,
	copied,
	onCopyLink,
	onShareTwitter,
	onShareTelegram,
	onShareReddit,
	onMakePublic
}: {
	isPublic: boolean;
	copied: boolean;
	onCopyLink: () => void;
	onShareTwitter: () => void;
	onShareTelegram: () => void;
	onShareReddit: () => void;
	onMakePublic: () => void;
}) {
	if (!isPublic) {
		return (
			<DropdownMenuItem
				className='cursor-pointer'
				onSelect={(e) => {
					e.preventDefault();
					onMakePublic();
				}}
			>
				<Globe className='h-4 w-4' />
				<span>Make public to share</span>
			</DropdownMenuItem>
		);
	}

	return (
		<>
			<DropdownMenuItem
				onClick={onCopyLink}
				className='cursor-pointer'
			>
				{copied ? <Check className='h-4 w-4 text-green-400' /> : <Link className='h-4 w-4' />}
				<span>{copied ? 'Copied!' : 'Copy link'}</span>
			</DropdownMenuItem>
			<DropdownMenuSeparator />
			<DropdownMenuItem
				onClick={onShareTwitter}
				className='cursor-pointer'
			>
				<XIcon className='h-4 w-4' />
				<span>Share on X</span>
			</DropdownMenuItem>
			<DropdownMenuItem
				onClick={onShareTelegram}
				className='cursor-pointer'
			>
				<TelegramIcon className='h-4 w-4' />
				<span>Share on Telegram</span>
			</DropdownMenuItem>
			<DropdownMenuItem
				onClick={onShareReddit}
				className='cursor-pointer'
			>
				<RedditIcon className='h-4 w-4' />
				<span>Share on Reddit</span>
			</DropdownMenuItem>
		</>
	);
}

export default function ConversationNavbarMenu() {
	const {
		conversationId,
		isPublicView,
		conversation,
		isPending,
		isPublic,
		actionsDisabled,
		isDataDeskOpen,
		setDataDeskOpen,
		copied,
		showDeleteDialog,
		setShowDeleteDialog,
		showVisibilityDialog,
		setShowVisibilityDialog,
		handleCopyLink,
		handleToggleVisibility,
		handleDelete,
		shareToTwitter,
		shareToTelegram,
		shareToReddit
	} = useConversationNavbar();

	if (!conversationId || isPending || !conversation) return null;

	return (
		<>
			{/* Share button */}
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant='ghost'
						size='sm'
						className='text-muted-foreground hover:text-foreground gap-1.5'
						disabled={actionsDisabled}
						aria-label='Share conversation'
					>
						<Share2 className='h-4 w-4' />
						<span className='hidden md:inline'>Share</span>
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align='end'
					className='w-48'
				>
					<ShareMenuItems
						isPublic={isPublic}
						copied={copied}
						onCopyLink={handleCopyLink}
						onShareTwitter={shareToTwitter}
						onShareTelegram={shareToTelegram}
						onShareReddit={shareToReddit}
						onMakePublic={() => setShowVisibilityDialog(true)}
					/>
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Overflow menu */}
			{!isPublicView ? (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant='ghost'
							size='icon-sm'
							className='text-muted-foreground hover:text-foreground'
							disabled={actionsDisabled}
							aria-label='Conversation options'
						>
							<MoreVertical className='h-4 w-4' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align='end'
						className='w-48'
					>
						<DropdownMenuItem
							className='cursor-pointer'
							onSelect={(e) => {
								e.preventDefault();
								setDataDeskOpen((prev) => !prev);
							}}
						>
							<Database className='h-4 w-4' />
							<span>{isDataDeskOpen ? 'Close Data Desk' : 'Data Desk'}</span>
						</DropdownMenuItem>

						<DropdownMenuSeparator />

						<DropdownMenuItem
							className='cursor-pointer'
							onSelect={(e) => {
								e.preventDefault();
								setShowVisibilityDialog(true);
							}}
						>
							{isPublic ? <Lock className='h-4 w-4' /> : <Globe className='h-4 w-4' />}
							<span>{isPublic ? 'Make Private' : 'Make Public'}</span>
						</DropdownMenuItem>

						<DropdownMenuSeparator />

						<DropdownMenuItem
							className='cursor-pointer text-red-400 focus:text-red-400'
							onSelect={(e) => {
								e.preventDefault();
								setShowDeleteDialog(true);
							}}
						>
							<Trash2 className='h-4 w-4' />
							<span>Delete</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			) : (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant='ghost'
							size='icon-sm'
							className='text-muted-foreground hover:text-foreground'
							aria-label='Options'
						>
							<MoreVertical className='h-4 w-4' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align='end'
						className='w-48'
					>
						<DropdownMenuItem
							className='cursor-pointer'
							onSelect={(e) => {
								e.preventDefault();
								setDataDeskOpen((prev) => !prev);
							}}
						>
							<Database className='h-4 w-4' />
							<span>{isDataDeskOpen ? 'Close Data Desk' : 'Data Desk'}</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}

			{/* Visibility confirmation dialog */}
			{!isPublicView && (
				<AlertDialog
					open={showVisibilityDialog}
					onOpenChange={setShowVisibilityDialog}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{isPublic ? 'Make Conversation Private?' : 'Make Conversation Public?'}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{isPublic
									? 'This will revoke public access. Anyone with the share link will no longer be able to view this conversation.'
									: 'This will make your conversation publicly accessible. Anyone with the link will be able to view the full conversation history.'}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={handleToggleVisibility}
								variant={isPublic ? 'destructive' : 'default'}
							>
								{isPublic ? 'Make Private' : 'Make Public'}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			)}

			{/* Delete confirmation dialog */}
			{!isPublicView && (
				<DeleteConversationDialog
					onDelete={handleDelete}
					open={showDeleteDialog}
					onOpenChange={setShowDeleteDialog}
				/>
			)}
		</>
	);
}
